/**
 * superAdmin.ts — Painel do Super Admin (F11).
 *
 * Acesso por PAPEL: o backend barra quem não é super_admin em todo o
 * prefixo /api/v1/super-admin.
 */

import type { components } from './generated/api'
import { api, apiLegado } from './http'

export type PainelSuperAdmin = components['schemas']['PainelSuperAdmin']
export type Prefeitura = components['schemas']['Prefeitura']
export type DetalhePrefeitura = components['schemas']['DetalhePrefeitura']
export type UsuarioSistema = components['schemas']['UsuarioSistema']
export type PrefeituraInput = components['schemas']['PrefeituraInput']
export type UsuarioInput = components['schemas']['UsuarioInput']
export type ProcessoComAuditoria = components['schemas']['ProcessoComAuditoria']

export function painelSuperAdmin() {
  return api<PainelSuperAdmin>('/api/v1/super-admin/painel')
}

export function criarPrefeitura(dados: PrefeituraInput) {
  return api<DetalhePrefeitura>('/api/v1/super-admin/prefeituras', {
    method: 'POST',
    body: dados,
  })
}

export function detalhePrefeitura(id: number) {
  return api<DetalhePrefeitura>(`/api/v1/super-admin/prefeituras/${id}`)
}

export function editarPrefeitura(id: number, dados: PrefeituraInput) {
  return api<DetalhePrefeitura>(`/api/v1/super-admin/prefeituras/${id}`, {
    method: 'PUT',
    body: dados,
  })
}

export function alternarPrefeitura(id: number) {
  return api<{ id: number; ativa: boolean }>(
    `/api/v1/super-admin/prefeituras/${id}/situacao`,
    { method: 'POST' },
  )
}

/** Prefeitura que o super admin assume no resto do sistema (fica na sessão). */
export function assumirPrefeitura(tenantId: number) {
  return api<{ id: number; municipio: string; orgao: string }>(
    '/api/v1/super-admin/prefeitura-ativa',
    { method: 'POST', body: { tenant_id: tenantId } },
  )
}

export function liberarPrefeitura() {
  return api<null>('/api/v1/super-admin/prefeitura-ativa', { method: 'DELETE' })
}

export function editarUsuario(id: number, dados: UsuarioInput) {
  return api<UsuarioSistema>(`/api/v1/super-admin/usuarios/${id}`, {
    method: 'PUT',
    body: dados,
  })
}

export function excluirUsuario(id: number) {
  return api<{ removido: boolean }>(`/api/v1/super-admin/usuarios/${id}`, {
    method: 'DELETE',
  })
}

export type ArquivosDeAuditoria = components['schemas']['ArquivosDeAuditoria']

export function arquivosDeAuditoria(processoId: number) {
  return api<ArquivosDeAuditoria>(
    `/api/v1/super-admin/auditoria-precos/${processoId}`,
  )
}

export function excluirArquivosDeAuditoria(arquivos: number[]) {
  return api<{ arquivos: number; arquivos_no_disco: number; bytes: number }>(
    '/api/v1/super-admin/auditoria-precos/arquivos',
    { method: 'DELETE', body: { arquivos } },
  )
}

export function limparAuditoriaDePrecos(processos: number[]) {
  return api<{
    processos: number
    arquivos: number
    bytes: number
    arquivos_no_disco: number
  }>('/api/v1/super-admin/auditoria-precos',
    { method: 'DELETE', body: { processos } },
  )
}

// ── PCA ────────────────────────────────────────────────────────────────────

export type PcaSalvo = components['schemas']['PcaSalvo']

export function pcasSalvos() {
  return api<{ pcas: PcaSalvo[] }>('/api/v1/super-admin/pca')
}

/**
 * Extração do PDF: rota LEGADA (pdfplumber + Gemini, ~250 linhas e até 2
 * minutos). A SPA reaproveita em vez de duplicar o pipeline — é o caso que
 * o §3 do plano prevê para `apiLegado`.
 */
export function extrairPca(arquivo: File) {
  const form = new FormData()
  form.append('file', arquivo)
  return apiLegado<{
    success: boolean
    data: Record<string, unknown>
    metadata: Record<string, unknown>
  }>('/api/pca/extract-llm', { method: 'POST', form })
}

export function salvarPca(
  tenantId: number,
  pcaJson: Record<string, unknown>,
  metadados: Record<string, unknown>,
) {
  return api<{ id: number; ano: number; situacao: string; itens: number }>(
    '/api/v1/super-admin/pca',
    {
      method: 'POST',
      body: { tenant_id: tenantId, pca_json: pcaJson, metadados },
    },
  )
}

export function excluirPca(id: number) {
  return api<{ removido: boolean }>(`/api/v1/super-admin/pca/${id}`, {
    method: 'DELETE',
  })
}
